import React, {useState} from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import styles from './SelfviewSettings.module.scss';

import {
    Button,
    CenterLayout,
    FontVariant,
    Icon,
    IconTypes,
    LoadingBox,
    NotificationTooltip,
    Text,
    type IconSource,
} from '@pexip/components';

import {TestId} from '../../../test/testIds';
import type {DeviceStatusInfo} from '../../types';

import {AspectRatioSelfview} from '../Selfview/AspectRatioSelfview/AspectRatioSelfview';
import {DeniedDevices} from '@pexip/media';

export const SelfViewSettings: React.FC<
    React.PropsWithChildren<
        Omit<
            React.ComponentProps<typeof AspectRatioSelfview>,
            'shouldShowUserAvatar' | 'username'
        > & {
            deviceStatusInfo: DeviceStatusInfo;
            isDisabled?: boolean;
            requestCameraPreview?: () => void;
            showPreviewLabel?: boolean;
            stopCameraPreview?: () => void;
            updatingPreviewAudio?: boolean;
            updatingPreviewVideo?: boolean;
        }
    >
> = ({
    children,
    deviceStatusInfo,
    isDisabled,
    requestCameraPreview,
    showPreviewLabel,
    stopCameraPreview,
    updatingPreviewAudio,
    updatingPreviewVideo,
    ...props
}) => {
    const {t} = useTranslation();
    const [tooltipVisible, setTooltipVisible] = useState(true);
    const [previewTooltipVisible, setPreviewTooltipVisible] = useState(false);

    const isCameraError =
        deviceStatusInfo.error === DeniedDevices.Both ||
        deviceStatusInfo.error === DeniedDevices.Camera;
    const requestBtn: {icon: IconSource; text: string; testid: string} =
        deviceStatusInfo.retriable
            ? {
                  icon: IconTypes.IconRefresh,
                  text: t('common.try-again', 'Try again'),
                  testid: TestId.SettingButtonTryAgain,
              }
            : {
                  icon: IconTypes.IconVideoOn,
                  text: t('settings.request-preview', 'Request preview'),
                  testid: TestId.SettingButtonRequestPreview,
              };
    const loading = updatingPreviewVideo || updatingPreviewAudio;

    return (
        <div className={styles.mainWrapper}>
            <AspectRatioSelfview
                aria-label={t('media.settings-selfview', 'Settings selfview')}
                aspectRatioWrapperClassName="mb-2"
                data-testid={TestId.SettingsVideoSelfview}
                labelContent={
                    <Text fontVariant={FontVariant.XSmall}>
                        <div className="d-flex align-items-center">
                            {t('settings.preview-label', 'Private preview')}
                            <NotificationTooltip
                                controlledIsVisible={previewTooltipVisible}
                                controlledOnChange={setPreviewTooltipVisible}
                                position="bottomEnd"
                                text={t(
                                    'setting.preview-label-info',
                                    'Only you can see this. Your camera remains off for everyone in the meeting.',
                                )}
                                ctaText={
                                    <Button
                                        modifier="fluid"
                                        variant="text"
                                        size="compact"
                                        onClick={() => {
                                            setPreviewTooltipVisible(false);
                                            stopCameraPreview?.();
                                        }}
                                    >
                                        {t(
                                            'setting.preview-label-action',
                                            'Stop preview',
                                        )}
                                    </Button>
                                }
                            >
                                <Button
                                    modifier="disc"
                                    variant="transparent"
                                    size="compact"
                                    onClick={() =>
                                        setPreviewTooltipVisible(v => !v)
                                    }
                                >
                                    <Icon
                                        source={IconTypes.IconInfoRound}
                                        size="compact"
                                    />
                                </Button>
                            </NotificationTooltip>
                        </div>
                    </Text>
                }
                labelTestId={TestId.SettingsVideoSelfviewPreviewLabel}
                showLabel={showPreviewLabel}
                wrapper-data-testid={TestId.SettingsVideoSelfviewWrapper}
                shouldShowUserAvatar={false}
                username={''}
                {...props}
            >
                {props.isVideoInputMuted && (
                    <>
                        <CenterLayout
                            className={cx(styles.mutedWrapper, {
                                [styles.mutedDisabled]: isDisabled,
                            })}
                        >
                            <LoadingBox
                                colorScheme="dark"
                                isLoading={loading}
                                sizeModifier="medium"
                            >
                                <Icon
                                    size="medium"
                                    colorScheme="dark"
                                    data-testid={
                                        deviceStatusInfo.error
                                            ? TestId.SettingIconWarning
                                            : TestId.SettingIconCameraOff
                                    }
                                    source={
                                        deviceStatusInfo.error
                                            ? IconTypes.IconAlert
                                            : IconTypes.IconVideoOff
                                    }
                                />
                                <Text
                                    colorScheme="dark"
                                    fontVariant={FontVariant.BodyBold}
                                >
                                    {deviceStatusInfo.error
                                        ? deviceStatusInfo.title
                                        : t(
                                              'settings.your-camera-is-off',
                                              'Your camera is off.',
                                          )}
                                </Text>
                            </LoadingBox>
                        </CenterLayout>
                        {!isDisabled && !loading && (
                            <div
                                className={cx(
                                    'd-flex flex-direction-column align-items-center',
                                    styles.tooltipButtonWrapper,
                                )}
                            >
                                <NotificationTooltip
                                    controlledIsVisible={tooltipVisible}
                                    controlledOnChange={setTooltipVisible}
                                    // FIXME: use `topCenter` instead of `topLeft` because of https://gitlab.com/pexip/zoo/-/issues/5494
                                    position="topCenter"
                                    header={deviceStatusInfo.title}
                                    text={deviceStatusInfo.message}
                                >
                                    <Button
                                        colorScheme="dark"
                                        onClick={requestCameraPreview}
                                        variant="tertiary"
                                        data-testid={requestBtn.testid}
                                        isDisabled={
                                            isCameraError &&
                                            !deviceStatusInfo.retriable
                                        }
                                        isLoading={loading}
                                        enhancerStart={
                                            <Icon source={requestBtn.icon} />
                                        }
                                    >
                                        {requestBtn.text}
                                    </Button>
                                </NotificationTooltip>
                            </div>
                        )}
                    </>
                )}
            </AspectRatioSelfview>
            <div className={styles.childrenWrapper}>{children}</div>
        </div>
    );
};
